import { camelToSnake, snakeToCamel } from "caseparser";

import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";

export const suscriptionApi = api
  .enhanceEndpoints({
    addTagTypes: [
      "subscriptions",
      "setupIntent",
      "paymentMethods",
      "paymentIntents",
      "invoices",
      "customer",
      "additionalProducts",
      "coupon",
      "activeSubscription",
    ],
  })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      activeSubscription: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/active`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["activeSubscription"],
      }),
      createSubscription: builder.mutation({
        query: ({ branchId, productId, coupon, additionalProducts }) => ({
          url: `branchs/${branchId}/subscriptions`,
          method: RequestMethod.POST,
          body: camelToSnake({
            productId,
            coupon,
            additionalProducts,
          }),
        }),
        invalidatesTags: ["subscriptions", "paymentMethods", "paymentIntents"],
      }),
      subscriptions: builder.query({
        query: ({ branchId, search }) => ({
          url: `branchs/${branchId}/subscriptions${search ? `?search=${search}` : ""}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["subscriptions"],
      }),
      additionalProducts: builder.query({
        query: ({ subscriptionId, branchId }) => ({
          url: `branchs/${branchId}/subscriptions/${subscriptionId}/additional-products`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["additionalProducts"],
      }),
      setupIntent: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/setup-intent`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["setupIntent", "customer"],
      }),
      paymentMethods: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/payment-methods`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["paymentMethods"],
      }),
      setPaymentMethod: builder.mutation({
        query: ({ branchId, paymentMethodId }) => ({
          url: `branchs/${branchId}/subscriptions/payment-methods/${paymentMethodId}`,
          method: RequestMethod.PUT,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        invalidatesTags: ["paymentMethods", "customer"],
      }),
      deletePaymentMethod: builder.mutation({
        query: ({ branchId, paymentMethodId }) => ({
          url: `branchs/${branchId}/subscriptions/payment-methods/${paymentMethodId}`,
          method: RequestMethod.DELETE,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        invalidatesTags: ["paymentMethods"],
      }),
      paymentIntents: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/payment-intents`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["paymentIntents"],
      }),
      invoices: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/invoices`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["invoices"],
      }),
      customer: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/customer`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["customer"],
      }),
      storeCustomer: builder.mutation({
        query: ({
          branchId,
          name,
          dni,
          email,
          phone,
          addressLine1,
          addressLine2,
          postalCode,
          country,
          city,
          state,
        }) => ({
          url: `branchs/${branchId}/subscriptions/store-customer`,
          method: RequestMethod.POST,
          body: camelToSnake({
            name,
            dni,
            email,
            phone,
            addressLine1,
            addressLine2,
            address_postal_code: postalCode,
            address_country: country,
            address_state: state,
            address_city: city,
          }),
        }),
        invalidatesTags: ["customer"],
      }),
      coupon: builder.query({
        query: ({ branchId, promotionCode }) => ({
          url: `branchs/${branchId}/subscriptions/coupon/${promotionCode}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["coupon"],
      }),
      products: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/products`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["coupon"],
      }),
      contracts: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/contracts`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["coupon"],
      }),
      sets: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/subscriptions/sets`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["coupon"],
      }),
    }),
  });

export const {
  useActiveSubscriptionQuery,
  useSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useSetupIntentQuery,
  useLazySetupIntentQuery,
  usePaymentMethodsQuery,
  useSetPaymentMethodMutation,
  useDeletePaymentMethodMutation,
  usePaymentIntentsQuery,
  useCustomerQuery,
  useStoreCustomerMutation,
  useAdditionalProductsQuery,
  useCouponQuery,
  useProductsQuery,
  useInvoicesQuery,
  useContractsQuery,
  useSetsQuery,
} = suscriptionApi;
